import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  DollarSign,
  PieChart,
  TrendingUp,
} from 'lucide-react';
import ValueRangeBar from './ValueRangeBar';
import EbitdaReconciliationTable from './EbitdaReconciliationTable';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatCompact = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return formatCurrency(value);
};

const formatPercent = (value) => `${(Number(value || 0) * 100).toFixed(0)}%`;

const ADJUSTMENT_REASONS = {
  owner_compensation_addback: 'Owner compensation above market rate',
  one_time_costs: 'Non-recurring expenses',
  normalization_addback: 'One-time adjustments to normalize operations',
  depreciation_addback: 'Non-cash depreciation expense',
};

const labelizeKey = (key) =>
  String(key)
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
    .join(' ');

const ValuationSnapshotExpertView = ({ snapshot }) => {
  const [methodologyExpanded, setMethodologyExpanded] = useState(false);
  const [inputsExpanded, setInputsExpanded] = useState(false);

  const inputs = snapshot?.inputs || {};
  const results = snapshot?.results || {};
  const overallRange = results.overall_range || { low: 0, mid: 0, high: 0 };
  // Real shape from SmbValuationService: multiples are nested under
  // `ebitda_method.multiples.{low,mid,high}`, NOT flattened. The values are
  // CEPA-adjusted when an assessment exists, raw benchmark multiples otherwise.
  const ebitdaMethod = results.ebitda_method || {};
  const ebitdaMultiples = ebitdaMethod.multiples || {};
  // benchmark_info exposes naics_code, sector_cluster, benchmark_family,
  // source_citation. There is no separate "benchmark multiple range" — the
  // applied multiples ARE the benchmark (CEPA-adjusted when applicable).
  const benchmarkInfo = results.benchmark_info || {};
  const valueAcceleration = results.value_acceleration;
  const revenueMethod = results.revenue_method;
  const adjustments = inputs.adjustments || {};

  const appliedMultipleLow = ebitdaMultiples.low || 0;
  const appliedMultipleMid = ebitdaMultiples.mid || 0;
  const appliedMultipleHigh = ebitdaMultiples.high || 0;
  const hasMultiple = appliedMultipleMid > 0;

  // Format the benchmark family/sector into a readable label.
  const benchmarkFamily = benchmarkInfo.benchmark_family || '';
  const sectorLabel = (benchmarkInfo.sector_cluster || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  // Source line — prefer source_citation, fall back to benchmark_family.
  const benchmarkSource = benchmarkInfo.source_citation || benchmarkFamily || '—';
  const naicsCode = benchmarkInfo.naics_code || inputs.industry_naics_code || '—';

  const adjustmentItems = Object.entries(adjustments)
    .filter(([, amount]) => amount && amount > 0)
    .map(([key, amount]) => ({
      label: labelizeKey(key),
      amount,
      reason: ADJUSTMENT_REASONS[key],
    }));

  // Range overlay on the headline value-bar uses the EBITDA method low/high
  // applied to the adjusted EBITDA — that's the "what the multiples imply"
  // band the CBV expects to see.
  const adjustedEbitda = ebitdaMethod.adjusted_ebitda || 0;
  const benchmarkLowValue = adjustedEbitda * appliedMultipleLow;
  const benchmarkHighValue = adjustedEbitda * appliedMultipleHigh;

  return (
    <div className="space-y-8">
      {/* Section A — Headline Panel */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl p-8 space-y-6">
        <div className="space-y-3">
          <div className="font-serif text-[32px] font-light text-[hsl(var(--foreground))]">
            Estimated value:{' '}
            <span className="font-medium">
              {formatCompact(overallRange.low)} – {formatCompact(overallRange.high)}
            </span>
          </div>
          <div className="text-[15px] text-[hsl(var(--muted-foreground))]">
            Mid: <span className="font-semibold text-[hsl(var(--foreground))]">{formatCompact(overallRange.mid)}</span>
            {valueAcceleration && (
              <>
                {' · '}
                <span>
                  CEPA-adjusted from {formatCompact(valueAcceleration.target_value)} (
                  {formatPercent(valueAcceleration.cepa_adjustment_pct)})
                </span>
              </>
            )}
          </div>
        </div>

        <ValueRangeBar
          low={overallRange.low}
          mid={overallRange.mid}
          high={overallRange.high}
          benchmarkLow={benchmarkLowValue || null}
          benchmarkHigh={benchmarkHighValue || null}
        />
      </div>

      {/* Section B — EBITDA Reconciliation */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl p-8 space-y-6">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-6 h-6 text-[hsl(var(--succeedo-orange))]" />
          <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">
            EBITDA Reconciliation
          </h2>
        </div>

        <EbitdaReconciliationTable
          revenue={inputs.revenue || 0}
          operatingExpenses={inputs.operating_expenses || 0}
          reportedEbitda={ebitdaMethod.reported_ebitda || 0}
          adjustments={adjustmentItems}
          adjustedEbitda={ebitdaMethod.adjusted_ebitda || 0}
        />
      </div>

      {/* Section C — Multiple Application */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl p-8 space-y-6">
        <div className="flex items-center gap-3">
          <DollarSign className="w-6 h-6 text-[hsl(var(--succeedo-orange))]" />
          <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">
            Multiple Application
          </h2>
        </div>

        <div className="space-y-4">
          {hasMultiple ? (
            <>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[14px] text-[hsl(var(--muted-foreground))] mb-1">Applied EBITDA multiple (mid)</div>
                  <div className="text-[32px] font-semibold text-[hsl(var(--foreground))]">
                    {Number(appliedMultipleMid).toFixed(1)}x
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[14px] text-[hsl(var(--muted-foreground))] mb-1">Range applied</div>
                  <div className="text-[18px] font-medium text-[hsl(var(--foreground))]">
                    {Number(appliedMultipleLow).toFixed(1)}x – {Number(appliedMultipleHigh).toFixed(1)}x
                  </div>
                </div>
              </div>

              {valueAcceleration ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[hsl(var(--succeedo-orange))]" />
                  <span className="px-3 py-1 bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/30 rounded-full text-[11px] font-mono uppercase text-[hsl(var(--succeedo-orange))] tracking-wide">
                    CEPA-adjusted multiple
                  </span>
                  <span className="text-[12px] text-[hsl(var(--muted-foreground))]">
                    Reflects the owner&rsquo;s readiness assessment.
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                  <span className="px-3 py-1 bg-[hsl(var(--muted))] border border-[hsl(var(--border))] rounded-full text-[11px] font-mono uppercase text-[hsl(var(--muted-foreground))] tracking-wide">
                    Raw industry benchmark multiple
                  </span>
                  <span className="text-[12px] text-[hsl(var(--muted-foreground))]">
                    No CEPA adjustment (assessment not applied).
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 py-2">
              <AlertTriangle className="w-4 h-4 text-[hsl(var(--succeedo-orange))]" />
              <span className="text-[13px] text-[hsl(var(--muted-foreground))]">
                EBITDA multiple not available — the calculation may have used a different primary method (revenue, DCF, or asset).
              </span>
            </div>
          )}

          <div className="pt-4 border-t border-[hsl(var(--border))] space-y-2">
            <div className="text-[13px] text-[hsl(var(--muted-foreground))]">
              <span className="font-semibold text-[hsl(var(--foreground))]">NAICS {naicsCode}</span>
              {sectorLabel && <> — {sectorLabel}</>}
            </div>
            <div className="text-[12px] text-[hsl(var(--muted-foreground))]">
              Source: {benchmarkSource}
            </div>
          </div>
        </div>
      </div>

      {/* Section D — CEPA Adjustment */}
      {valueAcceleration ? (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl p-8 space-y-6">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-[hsl(var(--succeedo-orange))]" />
            <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">
              CEPA Adjustment
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide">
                Readiness Score
              </div>
              <div className="text-[32px] font-semibold text-[hsl(var(--foreground))]">
                {valueAcceleration.cepa_score}
                <span className="text-[18px] text-[hsl(var(--muted-foreground))]"> / 100</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide">
                Applied Adjustment
              </div>
              <div className="text-[32px] font-semibold text-[hsl(var(--succeedo-orange))]">
                {formatPercent(valueAcceleration.cepa_adjustment_pct)}
              </div>
            </div>

            <div className="space-y-2">
              <div className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide">
                Value Impact
              </div>
              <div className="text-[18px] font-medium text-[hsl(var(--foreground))]">
                {formatCompact(valueAcceleration.gap_to_target)} reduction
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-[hsl(var(--border))]">
            <p className="text-[13px] text-[hsl(var(--muted-foreground))]">
              {valueAcceleration.cepa_adjustment_pct < 0
                ? 'Sub-target readiness reduces value. Addressing readiness gaps can unlock additional value.'
                : 'Above-target readiness increases value. Strong operational readiness commands a premium.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-[hsl(var(--muted))]/30 border border-[hsl(var(--border))] rounded-2xl p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-[hsl(var(--muted-foreground))]" />
            </div>
            <div className="flex-1">
              <h3 className="text-[18px] font-semibold text-[hsl(var(--foreground))] mb-2">
                No CEPA Adjustment Applied
              </h3>
              <p className="text-[14px] text-[hsl(var(--muted-foreground))]">
                Owner has not completed a Readiness Assessment, or it has not been shared with this team.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Section E — Revenue Mix */}
      {revenueMethod && (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl p-8 space-y-6">
          <div className="flex items-center gap-3">
            <PieChart className="w-6 h-6 text-[hsl(var(--succeedo-orange))]" />
            <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">
              Revenue Mix
            </h2>
          </div>

          <div className="space-y-4">
            <div className="h-16 flex rounded-lg overflow-hidden">
              <div
                className="bg-[hsl(var(--succeedo-orange))] flex items-center justify-center text-white text-[13px] font-medium"
                style={{ width: `${(revenueMethod.recurring_pct || 0) * 100}%` }}>
                {formatPercent(revenueMethod.recurring_pct)} Recurring
              </div>
              <div
                className="bg-[hsl(var(--muted))] flex items-center justify-center text-[hsl(var(--foreground))] text-[13px] font-medium"
                style={{ width: `${(1 - (revenueMethod.recurring_pct || 0)) * 100}%` }}>
                {formatPercent(1 - (revenueMethod.recurring_pct || 0))} Non-Recurring
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 pt-4 border-t border-[hsl(var(--border))]">
              <div className="space-y-2">
                <div className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide">
                  Recurring Revenue
                </div>
                <div className="text-[24px] font-semibold text-[hsl(var(--foreground))]">
                  {formatCompact((inputs.revenue || 0) * (revenueMethod.recurring_pct || 0))}
                </div>
              </div>
              <div className="space-y-2">
                <div className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide">
                  Non-Recurring Revenue
                </div>
                <div className="text-[24px] font-semibold text-[hsl(var(--foreground))]">
                  {formatCompact((inputs.revenue || 0) * (1 - (revenueMethod.recurring_pct || 0)))}
                </div>
              </div>
            </div>

            <p className="text-[13px] text-[hsl(var(--muted-foreground))] pt-2">
              Recurring revenue commands higher multiples due to predictability and lower customer acquisition costs.
            </p>
          </div>
        </div>
      )}

      {/* Section F — Methodology Notes */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl overflow-hidden">
        <button
          type="button"
          onClick={() => setMethodologyExpanded(!methodologyExpanded)}
          className="w-full px-8 py-6 flex items-center justify-between hover:bg-[hsl(var(--muted))]/30 transition-colors">
          <h2 className="font-serif text-[24px] font-light text-[hsl(var(--foreground))]">
            Methodology Notes
          </h2>
          {methodologyExpanded ? (
            <ChevronUp className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
          )}
        </button>

        {methodologyExpanded && (
          <div className="px-8 pb-8 space-y-6 border-t border-[hsl(var(--border))]">
            <div className="pt-6 space-y-4 text-[14px] text-[hsl(var(--foreground))] leading-relaxed">
              <div>
                <div className="font-semibold mb-2">EBITDA Calculation</div>
                <div className="font-mono text-[13px] bg-[hsl(var(--muted))]/30 p-4 rounded-lg space-y-1">
                  <div>EBITDA = Revenue − Operating Expenses + Adjustments</div>
                  <div>
                    EBITDA = {formatCurrency(inputs.revenue)} − {formatCurrency(inputs.operating_expenses)} +{' '}
                    {formatCurrency((ebitdaMethod.adjusted_ebitda || 0) - (ebitdaMethod.reported_ebitda || 0))}
                  </div>
                  <div className="font-semibold">
                    EBITDA = {formatCurrency(ebitdaMethod.adjusted_ebitda)}
                  </div>
                </div>
              </div>

              <div>
                <div className="font-semibold mb-2">Mid Value Calculation</div>
                <div className="font-mono text-[13px] bg-[hsl(var(--muted))]/30 p-4 rounded-lg space-y-1">
                  <div>Mid Value = Adjusted EBITDA × Mid Multiple</div>
                  <div>
                    Mid Value = {formatCurrency(ebitdaMethod.adjusted_ebitda)} ×{' '}
                    {Number(appliedMultipleMid).toFixed(1)}x
                  </div>
                  <div className="font-semibold">Mid Value = {formatCurrency(ebitdaMethod.mid)}</div>
                </div>
              </div>

              <div>
                <div className="font-semibold mb-2">Range Calculation</div>
                <div className="font-mono text-[13px] bg-[hsl(var(--muted))]/30 p-4 rounded-lg space-y-1">
                  <div>
                    Low = Adjusted EBITDA × {Number(appliedMultipleLow).toFixed(1)}x ={' '}
                    {formatCurrency(overallRange.low)}
                  </div>
                  <div>
                    Mid = Adjusted EBITDA × {Number(appliedMultipleMid).toFixed(1)}x ={' '}
                    {formatCurrency(overallRange.mid)}
                  </div>
                  <div>
                    High = Adjusted EBITDA × {Number(appliedMultipleHigh).toFixed(1)}x ={' '}
                    {formatCurrency(overallRange.high)}
                  </div>
                </div>
              </div>

              {valueAcceleration && (
                <div>
                  <div className="font-semibold mb-2">CEPA Adjustment</div>
                  <div className="font-mono text-[13px] bg-[hsl(var(--muted))]/30 p-4 rounded-lg space-y-1">
                    <div>Readiness Score: {valueAcceleration.cepa_score} / 100</div>
                    <div>Adjustment Factor: {formatPercent(valueAcceleration.cepa_adjustment_pct)}</div>
                    <div>Pre-adjustment Value: {formatCurrency(valueAcceleration.target_value)}</div>
                    <div className="font-semibold">
                      Post-adjustment Value: {formatCurrency(valueAcceleration.current_value)}
                    </div>
                  </div>
                </div>
              )}

              <p className="text-[13px] text-[hsl(var(--muted-foreground))] pt-4">
                Range is calculated using benchmark-derived multiples (±15% around mid, or benchmark band when wider).
                All calculations follow standard EBITDA multiple methodology as outlined in the Canadian Institute of
                Chartered Business Valuators (CICBV) practice standards.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Section G — Inputs Reference */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl overflow-hidden">
        <button
          type="button"
          onClick={() => setInputsExpanded(!inputsExpanded)}
          className="w-full px-8 py-6 flex items-center justify-between hover:bg-[hsl(var(--muted))]/30 transition-colors">
          <h2 className="font-serif text-[24px] font-light text-[hsl(var(--foreground))]">
            Inputs Reference
          </h2>
          {inputsExpanded ? (
            <ChevronUp className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
          )}
        </button>

        {inputsExpanded && (
          <div className="px-8 pb-8 border-t border-[hsl(var(--border))]">
            <div className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]">
                <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Revenue</span>
                <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">
                  {formatCurrency(inputs.revenue)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]">
                <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Operating Expenses</span>
                <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">
                  {formatCurrency(inputs.operating_expenses)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]">
                <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Recurring Revenue %</span>
                <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">
                  {formatPercent(inputs.recurring_revenue_pct)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]">
                <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Growth Rate</span>
                <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">
                  {formatPercent(inputs.growth_rate)}
                </span>
              </div>
              {inputs.growth_source && (
                <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]">
                  <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Growth Source</span>
                  <span className="text-[14px] font-medium text-[hsl(var(--foreground))] capitalize">
                    {String(inputs.growth_source).replace(/_/g, ' ')}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]">
                <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Tangible Assets</span>
                <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">
                  {formatCurrency(inputs.tangible_assets)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]">
                <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Liabilities</span>
                <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">
                  {formatCurrency(inputs.liabilities)}
                </span>
              </div>
              {inputs.industry_naics_code && (
                <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]">
                  <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Industry (NAICS)</span>
                  <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">
                    {inputs.industry_naics_code}
                  </span>
                </div>
              )}
              {inputs.sector_cluster && (
                <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]">
                  <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Sector Cluster</span>
                  <span className="text-[14px] font-medium text-[hsl(var(--foreground))] capitalize">
                    {String(inputs.sector_cluster).replace(/_/g, ' ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ValuationSnapshotExpertView.propTypes = {
  snapshot: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    preview_metrics: PropTypes.shape({
      primary: PropTypes.string,
      secondary: PropTypes.string,
      tertiary: PropTypes.string,
    }),
    inputs: PropTypes.object,
    results: PropTypes.object,
  }).isRequired,
};

export default ValuationSnapshotExpertView;
